import { useCallback, useEffect, useRef, useState } from "react";
import { ErrorResponse } from "../types/errorResponse";
import { ModalMessageSimple } from "../components/ModalMessageSimple";
import { createRoot, Root } from "react-dom/client";

interface UseModalMessagesSimpleDefaultOptions {
  isError?: boolean;
}

const useModalMessagesSimple = (defaultOptions?: UseModalMessagesSimpleDefaultOptions) => {
  const [internalMessages, setInternalMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState(defaultOptions?.isError || false);

  const rootRef = useRef<Root | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const setMessages = useCallback((
    messages: string[] | ErrorResponse | boolean,
    options?: {
      isError?: boolean;
      errorResponse?: boolean;
    }
  ) => {
    let parsedMessages;

    if (options?.errorResponse) {
      parsedMessages = (messages as ErrorResponse)?.response?.data?.errors?.base;
    } else {
      parsedMessages = messages as string[];
    }

    if (parsedMessages) {
      setInternalMessages(parsedMessages);

      if (options?.isError != null) {
        setIsError(options?.isError);
      }
    }
  }, []);

  const clearMessages = useCallback(() => {
    setInternalMessages(false);
    setIsError(defaultOptions?.isError || false);
  }, [defaultOptions?.isError]);

  useEffect(() => {
    const div = document.createElement('div');

    document.body.appendChild(div);

    containerRef.current = div;
    rootRef.current = createRoot(div);

    return () => {
      const root = rootRef.current;
      const container = div;

      setTimeout(() => {
        root?.unmount();
        container.remove();
      }, 0);
    };
  }, []);

  useEffect(() => {
    rootRef.current?.render(
      <ModalMessageSimple
        messages={internalMessages}
        setMessages={() => {
          setInternalMessages(false);
          setIsError(defaultOptions?.isError || false);
        }}
        isError={isError}
      />
    );
  }, [internalMessages, isError, defaultOptions?.isError]);

  return {
    messages: internalMessages,
    setMessages,
    clearMessages,
    setIsError,
  };
}

export default useModalMessagesSimple;
